/**
 * Utility functions for handling dates from Firestore
 */

/**
 * Safely parse a date from Firestore, handling both Timestamp objects and string dates
 * @param {any} dateValue - The date value from Firestore (could be Timestamp, string, or null)
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
export function parseFirestoreDate(dateValue) {
  if (!dateValue) return null;
  
  try {
    // Handle Firestore Timestamp objects
    if (typeof dateValue === 'object' && dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    
    // Handle string dates or other formats
    const date = new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('Error parsing date:', error, dateValue);
    return null;
  }
}

/**
 * Format a Firestore date for display
 * @param {any} dateValue - The date value from Firestore
 * @param {string} fallback - Fallback text if date is invalid (default: 'Date unavailable')
 * @returns {string} - Formatted date string or fallback
 */
export function formatFirestoreDate(dateValue, fallback = 'Date unavailable') {
  const date = parseFirestoreDate(dateValue);
  return date ? date.toLocaleDateString() : fallback;
}

/**
 * Format a Firestore date for display with time
 * @param {any} dateValue - The date value from Firestore
 * @param {string} fallback - Fallback text if date is invalid (default: 'Date unavailable')
 * @returns {string} - Formatted date and time string or fallback
 */
export function formatFirestoreDateTime(dateValue, fallback = 'Date unavailable') {
  const date = parseFirestoreDate(dateValue);
  return date ? date.toLocaleString() : fallback;
}
